// Import your actual audio files here (replace with real paths or use a public folder)
// import startupLoan from './audio/startup-loan.mp3';
// import bridgeLoans from './audio/bridge-loans.mp3';
// import goldLoan from './audio/gold-loan.mp3';
// import homeEquity from './audio/home-equity.mp3';
// import dataCollection from './audio/data-collection.mp3';
// import investmentProperty from './audio/investment-property.mp3';
// import autoLoans from './audio/auto-loans.mp3';
// import refinancing from './audio/refinancing.mp3';
// import receptionist from './audio/receptionist.mp3';
// import debtCollection from './audio/debt-collection.mp3';
// import dealership from './audio/dealership.mp3';
// import loan from './audio/loan.mp3';
// import leadGeneration from './audio/lead-generation.mp3';
// import hospital from './audio/hospital.mp3';
// import school from './audio/school.mp3';
// import retail from './audio/retail.mp3';
// import realEstate from './audio/real-estate.mp3';
// import hospitality from './audio/hospitality.mp3';
// import travelTourism from './audio/travel-tourism.mp3';
// import telecom from './audio/telecommunications.mp3';
// import customerSupport from './audio/customer-support.mp3';
// import nonProfit from './audio/non-profit.mp3';
// import legalServices from './audio/legal-services.mp3';
// import fitness from './audio/fitness-wellness.mp3';
// import eventMgmt from './audio/event-management.mp3';
// import logistics from './audio/logistics-delivery.mp3';
// import govtServices from './audio/government-services.mp3';
// import entertainment from './audio/entertainment.mp3';
// import manufacturing from './audio/manufacturing.mp3';

import React, { useState, useRef, useEffect } from 'react';
import styles from './ExploreVoices.module.css';

// Optional: Add real audio files later (just import them here)
const audioImports = {
  // Uncomment and add when you have real files
  // 'Startup Loan': require('./audio/startup-loan.mp3'),
  // 'Bridge Loans': require('./audio/bridge-loans.mp3'),
  // ... add others when ready
};

const voiceData = [
  'Startup Loan', 'Bridge Loans', 'Gold Loan',
  'Home Equity Loans', 'Data Collection', 'Investment Property Loans',
  'Auto Loans', 'Refinancing Services', 'Receptionist',
  'Debt Collection', 'Dealership', 'Loan',
  'Lead Generation', 'Hospital', 'School',
  'Retail', 'Real Estate', 'Hospitality',
  'Travel and Tourism', 'Telecommunications', 'Customer Support',
  'Non-Profit', 'Legal Services', 'Fitness and Wellness',
  'Event Management', 'Logistics and Delivery', 'Government Services',
  'Entertainment', 'Manufacturing', 'Startup Loan'
];

const fakeDurations = ['01:24', '02:15', '00:58', '01:47', '01:12', '02:03', '01:35', '01:59', '00:45', '02:30'];

const ExploreVoices = () => {
  const [playingIndex, setPlayingIndex] = useState(null);
  const [progress, setProgress] = useState({});
  const [currentTime, setCurrentTime] = useState({});
  const [duration, setDuration] = useState({});
  const audioRefs = useRef([]);

  // Detect if real audio exists for this title
  const hasRealAudio = (title) => !!audioImports[title];

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '00:00';
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = Math.floor(seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const togglePlay = (index) => {
    const title = voiceData[index];
    const hasAudio = hasRealAudio(title);

    if (playingIndex === index) {
      // Pause
      if (hasAudio) audioRefs.current[index]?.pause();
      setPlayingIndex(null);
      return;
    }

    // Pause previous
    if (playingIndex !== null && hasRealAudio(voiceData[playingIndex])) {
      audioRefs.current[playingIndex]?.pause();
    }

    setPlayingIndex(index);

    if (hasAudio) {
      // Real audio mode
      const audio = audioRefs.current[index];
      audio.currentTime = 0;
      audio.play();
    } else {
      // Fake playback animation
      let progressVal = 0;
      const totalSec = fakeDurations[index % fakeDurations.length]
        .split(':')
        .reduce((acc, t) => acc * 60 + +t, 0);

      const interval = setInterval(() => {
        progressVal += 1;
        setProgress(p => ({ ...p, [index]: progressVal }));
        setCurrentTime(p => ({ ...p, [index]: (progressVal / 100) * totalSec }));

        if (progressVal >= 100) {
          clearInterval(interval);
          setTimeout(() => {
            setPlayingIndex(null);
            setProgress(p => ({ ...p, [index]: 0 }));
            setCurrentTime(p => ({ ...p, [index]: 0 }));
          }, 300);
        }
      }, 80);
    }
  };

  return (
    <section className={styles.container}>
      <h2 className={styles.title}>Explore Audio Features</h2>

      <div className={styles.grid}>
        {voiceData.map((title, index) => {
          const hasAudio = hasRealAudio(title);
          const fakeDur = fakeDurations[index % fakeDurations.length];

          return (
            <div key={`${title}-${index}`} className={styles.card}>
              <h3 className={styles.cardTitle}>{title}</h3>

              <div className={styles.player}>
                <button
                  onClick={() => togglePlay(index)}
                  className={`${styles.playButton} ${playingIndex === index ? styles.playing : ''}`}
                >
                  {playingIndex === index ? (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="4" width="4" height="16" />
                      <rect x="14" y="4" width="4" height="16" />
                    </svg>
                  ) : (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 5v14l11-7L8 5z" />
                    </svg>
                  )}
                </button>

                <div className={styles.progressContainer}>
                  <div className={styles.progressBar}>
                    <div
                      className={styles.progressFill}
                      style={{ width: `${progress[index] || 0}%` }}
                    />
                  </div>
                  <span className={styles.duration}>
                    {hasAudio
                      ? `${formatTime(currentTime[index] || 0)} / ${duration[index] || '00:00'}`
                      : `${formatTime(currentTime[index] || 0)} / ${fakeDur}`}
                  </span>
                </div>

                {/* Hidden real audio element - only renders if file exists */}
                {hasAudio && (
                  <audio
                    ref={el => audioRefs.current[index] = el}
                    src={audioImports[title]}
                    onTimeUpdate={() => {
                      const audio = audioRefs.current[index];
                      setProgress(p => ({ ...p, [index]: (audio.currentTime / audio.duration) * 100 }));
                      setCurrentTime(p => ({ ...p, [index]: audio.currentTime }));
                    }}
                    onLoadedMetadata={() => {
                      const audio = audioRefs.current[index];
                      setDuration(p => ({ ...p, [index]: formatTime(audio.duration) }));
                    }}
                    onEnded={() => {
                      setPlayingIndex(null);
                      setProgress(p => ({ ...p, [index]: 0 }));
                    }}
                  />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default ExploreVoices;